import { Button, OTPInput, Typography } from '@themenu/design-system'
import { type FC, useCallback, useEffect, useId, useState } from 'react'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { environmentVariables } from '../../../utils/environmentVariables'
import { ActivationStepLayout } from './ActivationStepLayout'

const PIN_LENGTH = 4
const PIN_FORGOTTEN_URL =
  'https://ajuda.swile.com.br/hc/pt-br/articles/4410258438039-Como-redefinir-sua-senha-no-app-Swile'

/**
 * PIN substep of the BR activation funnel. A masked `OTPInput` collects the
 * 4-digit PIN, auto-submitting on the 4th digit via `/pin`. Surfaces a generic
 * anti-enumeration error only.
 *
 * NOTE: kept named `BrCredentialsForm` (was the two-substep identifier+PIN form);
 * the de-BR-ification/rename is a separate planned genericization sweep.
 */
export type BrCredentialsFormProps = {
  /** Submit the 4-digit PIN → `/pin`. */
  onPinSubmit: (pin: string) => Promise<void>
  /** Generic error id (anti-enum). */
  errorMessageId?: string | null
  /** Rich-text format values merged into the error message (e.g. a link tag). */
  errorFormatValues?: Record<string, ReactNode | ((chunks: ReactNode[]) => ReactNode)>
  /** Back from the PIN substep to the `/signin` on-ramp. */
  onBack: () => void
}

export const BrCredentialsForm: FC<BrCredentialsFormProps> = ({ onPinSubmit, errorMessageId = null, errorFormatValues, onBack }) => {
  const { formatMessage } = useIntl()
  const errorId = useId()
  const pinDescriptionId = useId()

  const [pin, setPin] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Clear the PIN when the parent surfaces an error so the stale value is
  // removed and auto-submit can fire again on re-entry (mirrors ActivationOtpForm).
  useEffect(() => {
    if (errorMessageId) {
      setPin('')
    }
  }, [errorMessageId])

  const handlePinChange = useCallback(
    async (value: string) => {
      const trimmed = value.trim()
      setPin(trimmed)
      if (trimmed.length === PIN_LENGTH && !submitting) {
        setSubmitting(true)
        try {
          await onPinSubmit(trimmed)
        } finally {
          setSubmitting(false)
        }
      }
    },
    [onPinSubmit, submitting],
  )

  return (
    <ActivationStepLayout
      titleId="br_activation.pin.title"
      errorId={errorId}
      errorMessageId={errorMessageId}
      errorFormatValues={errorFormatValues}
      showBackButton
      onBack={onBack}
      footer={
        <>
          <Button
            variant="secondary"
            className="w-full"
            component="a"
            href={PIN_FORGOTTEN_URL}
            data-testid="br-activation-pin-forgotten"
          >
            {formatMessage({ id: 'br_activation.pin.forgotten' })}
          </Button>
          <Button
            variant="alternativeText"
            className="w-full"
            component="a"
            href={environmentVariables('BR_HELP_URL')}
            data-testid="br-activation-pin-help"
          >
            {formatMessage({ id: 'help.faq' })}
          </Button>
        </>
      }
    >
      <Typography variant="body" id={pinDescriptionId}>
        {formatMessage({ id: 'br_activation.pin.description' })}
      </Typography>
      <div data-testid="br-activation-pin-input">
        <OTPInput
          autoFocus
          length={PIN_LENGTH}
          value={pin}
          onChange={handlePinChange}
          type="password"
          aria-labelledby={pinDescriptionId}
          aria-label={formatMessage({ id: 'br_activation.pin.aria_label' })}
        />
      </div>
    </ActivationStepLayout>
  )
}
